import * as t from 'io-ts';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {delayAsync} from '../../../src/helpers/asyncHelpers';
import {ApiType, ModelType} from '../../../src/models/api/apiTypes';
import {debug} from '../../../src/services/debug';
import {fetchApiAsync} from '../../../src/services/http/http';
import {HttpSendOptions} from '../../../src/services/http/httpBase';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {CacheResponseInfo, OfflineResourceCache} from './offlineResourcesCommon';

/*
 * Provides the various offline resource fetching strategies.
 */

interface FetchCachedApiAsyncParams<T extends t.Mixed> {
  apiType: ApiType<T>;
  resourceId: number;
  path: string;
  getLatestTimestamp: () => number | undefined;
  httpOptions?: HttpSendOptions;
  onCachedResponseReceived: HandlerOf<CacheResponseInfo>;
  cacheTimestamp?: number;
  onUpdatedNetworkResponse: (response: ModelType<T>) => void;
  onNetworkError: (error: any) => void;
}

/** GET the resource by id from the cache and server, whichever is faster, and update the cache without blocking. */
export async function fetchAndCacheApiAsync<T extends t.Mixed>({
  apiType,
  resourceId,
  path,
  getLatestTimestamp,
  httpOptions,
  onCachedResponseReceived,
  cacheTimestamp,
  onUpdatedNetworkResponse,
  onNetworkError,
}: FetchCachedApiAsyncParams<T>): Promise<ModelType<T>> {
  const networkPromise = fetchThenCacheApiAsync({
    apiType,
    resourceId,
    path,
    httpOptions,
    cacheTimestamp,
  });

  // We add a delayAsync to ensure the result will be on another macro-task
  // Fetching the cache with indexDB is asynchronous but on mobile, fetching the cache is synchronous
  // This ensures that navigating to P3 will be done before loading all the P3 data
  const cachePromise = Promise.all([
    readFromCacheAsync(apiType, resourceId, getLatestTimestamp, onCachedResponseReceived),
    delayAsync(0, httpOptions?.cancelToken),
  ]).then(([data]) => data);

  // Use the first successful response.
  const firstResponse = await Promise.any([networkPromise, cachePromise])
    // If both promises are rejected, use the error from the network promise.
    .catch(() => networkPromise);

  // Use the network response regardless, but without blocking.
  networkPromise.then(
    (networkResponse) => {
      if (firstResponse !== networkResponse) {
        // We got an updated response from the network but we had returned the cached response.
        onUpdatedNetworkResponse(networkResponse);
      }
    },
    (error) => onNetworkError(error),
  );

  return firstResponse;
}

async function readFromCacheAsync<T extends t.Mixed>(
  apiType: ApiType<T>,
  resourceId: number,
  getLatestTimestamp: () => number | undefined,
  onCachedResponseReceived: HandlerOf<CacheResponseInfo>,
): Promise<ModelType<T>> {
  const cache = offlineCaches.get(apiType);
  if (!cache) {
    throw new CacheFailureError();
  }

  const cachedResponse = await cache.readIfLatestAsync(resourceId, getLatestTimestamp).catch((error) => {
    reportToBugsnag(error);
    return undefined;
  });

  if (!cachedResponse) {
    throw new CacheFailureError();
  }

  const cacheInfo = cache.getCacheResponseInfo(resourceId, cachedResponse);
  if (cacheInfo) {
    onCachedResponseReceived(cacheInfo);
  }

  return cachedResponse;
}

interface FetchThenCacheApiAsyncParams<T extends t.Mixed> {
  apiType: ApiType<T>;
  resourceId: number;
  path: string;
  httpOptions?: HttpSendOptions;
  cacheTimestamp?: number;
}

/** Bypass the cache and fetch from the server, but cache the result without blocking. */
export async function fetchThenCacheApiAsync<T extends t.Mixed>({
  apiType,
  resourceId,
  path,
  httpOptions,
  cacheTimestamp,
}: FetchThenCacheApiAsyncParams<T>): Promise<ModelType<T>> {
  const cache = offlineCaches.get(apiType);
  const serverResponse = await fetchApiAsync(apiType, path, httpOptions);

  if (cache) {
    debug('offline', 'fetchThenCacheApiAsync', 'refresh cache entry');
    cache.addAsync(resourceId, serverResponse, cacheTimestamp).catch((error) => {
      reportToBugsnag(new ExtendedError(`Failed to cache API result ${apiType.name}[${resourceId}]`, error), {
        metadata: {
          assetSize: JSON.stringify(serverResponse).length,
        },
      });
    });
  }

  return serverResponse;
}

const offlineCaches: Map<ApiType<t.Mixed>, OfflineResourceCache<number | string, object>> = new Map();

export function registerOfflineCacheForFetching(offlineCache: OfflineResourceCache<number | string, object>) {
  offlineCaches.set(offlineCache.apiType, offlineCache);
}

/*
 * Helpers.
 */

class CacheFailureError extends Error {
  constructor() {
    super('Cache failure.');
  }
}
